/**
 * Chat usage strip model: chats, analysis credits and voice minutes (split out
 * of ChatScreenEnhanced). Every number comes from the server (profile usage,
 * credit status, transcription usage, trial status); nothing here guesses a
 * plan limit (CHAT-21). Pure module so it can be unit-tested.
 */
import type { MaterialIcons } from '@expo/vector-icons';

import type { TrialStatus } from '../../context/TrialContext';
import type { CreditStatus, TranscriptionUsageResponse } from '../../types';
import { isUnlimitedLimit } from './chatConstants';

type TFn = (key: string, defaultOrVars?: string | Record<string, unknown>, vars?: Record<string, unknown>) => string;

export interface ChatQuotaStatus {
  chatLimitExceeded: boolean;
  transcriptLimitExceeded: boolean;
  usage: {
    daily_chats_used: number;
    daily_chats_limit: number;
    monthly_chats_used: number;
    monthly_chats_limit: number;
    monthly_transcripts_used: number;
    monthly_transcripts_limit: number;
  };
}

export interface ChatTrialUsage {
  used: number;
  remaining: number;
  total: number;
  hoursLeft: number;
  analysisUsed: number;
  analysisRemaining: number;
  analysisTotal: number;
  voiceUsed: number;
  voiceRemaining: number;
  voiceTotal: number;
}

export interface ChatUsageCard {
  key: 'trial-chat' | 'chat' | 'analysis' | 'voice';
  icon: keyof typeof MaterialIcons.glyphMap;
  label: string;
  value: string;
  meta: string;
  /** 0-100 */
  percent: number;
}

export function trialUsageFrom(trialStatus: TrialStatus | null | undefined): ChatTrialUsage | null {
  if (!trialStatus?.active) return null;
  return {
    used: Math.max(0, trialStatus.chat_count ?? 0),
    remaining: Math.max(0, trialStatus.messages_remaining ?? 0),
    total: Math.max(
      (trialStatus.chat_count ?? 0) + (trialStatus.messages_remaining ?? 0),
      trialStatus.chat_count ?? 0,
      1,
    ),
    hoursLeft: Math.max(0, Math.round(trialStatus.hours_remaining ?? 0)),
    analysisUsed: Math.max(0, trialStatus.analysis_used ?? 0),
    analysisRemaining: Math.max(0, trialStatus.analysis_remaining ?? 0),
    analysisTotal: Math.max(1, trialStatus.analysis_limit ?? 1),
    voiceUsed: Math.max(0, trialStatus.voice_minutes_used ?? 0),
    voiceRemaining: Math.max(0, trialStatus.voice_minutes_remaining ?? 0),
    voiceTotal: Math.max(0, trialStatus.voice_minutes_limit ?? 0),
  };
}

export function creditChipLabel(t: TFn, creditStatus: CreditStatus | null | undefined): string {
  if (creditStatus?.usage_hidden || creditStatus?.pool_available) return t('chat.webSchoolPlan');
  if (creditStatus?.credit_exempt) return t('dashboard.unlimited');
  if (creditStatus) return `${creditStatus.total_available ?? 0} ${t('chat.webCredits')}`;
  return t('chat.webCreditStatus');
}

export function clampPercent(used: number, total: number): number {
  if (!Number.isFinite(used) || !Number.isFinite(total) || total <= 0 || isUnlimitedLimit(total)) return 0;
  return Math.max(0, Math.min(100, (used / total) * 100));
}

export function buildChatUsageCards({
  t,
  trialUsage,
  quotaStatus,
  quotaLoadFailed,
  creditStatus,
  transcriptionUsage,
}: {
  t: TFn;
  trialUsage: ChatTrialUsage | null;
  quotaStatus: ChatQuotaStatus | null;
  quotaLoadFailed: boolean;
  creditStatus: CreditStatus | null | undefined;
  transcriptionUsage: TranscriptionUsageResponse | null;
}): ChatUsageCard[] {
  const unlimitedLabel = t('dashboard.unlimited');
  const formatQuotaValue = (used: number, limit: number) =>
    isUnlimitedLimit(limit) ? `${used}/${unlimitedLabel}` : `${used}/${limit}`;
  const formatMinutes = (seconds?: number) => {
    const minutes = Math.max(0, Math.ceil((seconds ?? 0) / 60));
    return `${minutes} ${t('common.minutesShort')}`;
  };
  const analysisCredits = creditStatus?.subscription_credits;
  const transcriptionTotalSeconds = transcriptionUsage
    ? transcriptionUsage.subscription_limit_seconds + transcriptionUsage.purchased_granted_seconds
    : 0;
  const transcriptionUsedSeconds = transcriptionUsage
    ? transcriptionUsage.subscription_used_seconds + transcriptionUsage.purchased_used_seconds
    : 0;
  const transcriptionUnlimited = Boolean(
    transcriptionUsage && isUnlimitedLimit(transcriptionUsage.subscription_limit_minutes)
  );
  const analysisUnlimited = creditStatus?.credit_exempt === true;
  const creditLabel = creditChipLabel(t, creditStatus);

  return [
    trialUsage
      ? {
          key: 'trial-chat',
          icon: 'science',
          label: t('chat.trialUsageLabel'),
          value: t('chat.trialChatsValue', { used: trialUsage.used, total: trialUsage.total }),
          meta: t('chat.trialUsageRemaining', { remaining: trialUsage.remaining, hours: trialUsage.hoursLeft }),
          percent: clampPercent(trialUsage.used, trialUsage.total),
        }
      : {
          key: 'chat',
          icon: 'chat-bubble-outline',
          label: t('chat.webUsageChat'),
          // The daily limit comes from the server; if that call failed we
          // don't know it, so show a dash rather than a guessed plan limit.
          value: quotaStatus
            ? formatQuotaValue(quotaStatus.usage.daily_chats_used, quotaStatus.usage.daily_chats_limit)
            : quotaLoadFailed
              ? '—'
              : t('common.loading'),
          meta: quotaLoadFailed
            ? t('chat.webUsageCountUnavailable')
            : t('chat.webUsageDaily'),
          percent: quotaStatus
            ? clampPercent(quotaStatus.usage.daily_chats_used, quotaStatus.usage.daily_chats_limit)
            : 0,
        },
    {
      key: 'analysis',
      icon: 'verified',
      label: trialUsage ? t('chat.trialAnalysisLabel') : t('chat.webUsageAnalysis'),
      value: trialUsage ? `${trialUsage.analysisUsed}/${trialUsage.analysisTotal}` : creditLabel,
      meta: trialUsage
        ? t('chat.trialAnalysisRemaining', { remaining: trialUsage.analysisRemaining, total: trialUsage.analysisTotal })
        : analysisUnlimited
          ? t('chat.webUsageUnlimitedAnalysis')
          : creditStatus?.usage_hidden || creditStatus?.pool_available
            ? t('chat.webSchoolPlanIncluded')
            : analysisCredits
              ? `${analysisCredits.used}/${analysisCredits.total} ${t('chat.webUsageMonthly')}`
              : t('chat.webUsageCredits'),
      percent: trialUsage
        ? clampPercent(trialUsage.analysisUsed, trialUsage.analysisTotal)
        : analysisUnlimited || !analysisCredits
          ? 0
          : clampPercent(analysisCredits.used, analysisCredits.total),
    },
    {
      key: 'voice',
      icon: 'graphic-eq',
      label: trialUsage ? t('chat.trialVoiceLabel') : t('chat.webUsageVoice'),
      value: trialUsage && trialUsage.voiceTotal > 0
        ? `${trialUsage.voiceRemaining} ${t('common.minutesShort')} ${t('common.remaining')}`
        : transcriptionUsage
          ? transcriptionUnlimited
            ? unlimitedLabel
            : `${formatMinutes(transcriptionUsage.total_remaining_seconds)} ${t('common.remaining')}`
          : t('common.loading'),
      meta: trialUsage && trialUsage.voiceTotal > 0
        ? t('chat.trialVoiceUsage', { used: trialUsage.voiceUsed, total: trialUsage.voiceTotal })
        : transcriptionUsage
          ? transcriptionUnlimited
            ? `${formatMinutes(transcriptionUsedSeconds)} ${t('chat.webUsageUsed')}`
            : `${formatMinutes(transcriptionTotalSeconds)} ${t('chat.webUsageTotal')}`
          : t('chat.webUsageTranscription'),
      percent: trialUsage && trialUsage.voiceTotal > 0
        ? clampPercent(trialUsage.voiceUsed, trialUsage.voiceTotal)
        : transcriptionUsage && !transcriptionUnlimited
          ? clampPercent(transcriptionUsedSeconds, transcriptionTotalSeconds)
          : 0,
    },
  ];
}
